/**
 * useDirtyCheck — API에서 받은 원본 데이터와 현재 폼 값을 비교해 변경 여부를 감지
 *
 * 값 비교는 타입에 관대하다 — useAdminForm 은 폼 값을 항상 string 으로 다루기 때문에
 * (select 의 number 필드, JSON 객체 필드 등) 서버 원본과 타입이 달라도 값만 같으면
 * 같은 것으로 취급한다.
 * - 원시값: number 3 ↔ string "3" 처럼 타입만 다르면 문자열로 변환해 비교한다.
 * - 객체/배열: 한쪽이 JSON.stringify 된 문자열이어도(예: address_json) 파싱해서
 *   구조적으로(키 순서 무관) 비교한다.
 *
 * @example
 * // number 필드를 select 로 다뤄서 폼 값은 string 이어도 정상 비교된다
 * // original.partner_key: number(3), form.values.partner_key: string("3") → 같은 값으로 판정
 * const { isDirty } = useDirtyCheck(original, form.values);
 *
 * @example
 * // 기본 사용 — 수정 폼에서 변경 여부 감지
 * // original 은 API 원본(더 많은 필드를 가진 타입)이어도 된다 — form.values 에 있는 키만 비교한다.
 * const { data: original } = useApiQuery({ queryKey: [...], url: "..." });
 * const form = useAdminForm({ name: "", email: "" });
 *
 * const { isDirty, dirtyFields, diff } = useDirtyCheck(original, form.values);
 *
 * // isDirty    → true/false (하나라도 바뀌면 true)
 * // dirtyFields → { name: true, email: false }
 * // diff       → { name: "새이름" }  (바뀐 필드만 — PATCH 요청에 사용)
 *
 * @example
 * // PATCH — 변경된 필드만 전송
 * const handleUpdate = () => {
 *   if (!isDirty) return;
 *   updateMutation.mutate({ url: `/users/${id}`, method: "patch", data: diff });
 * };
 *
 * @example
 * // 저장하지 않은 변경사항 경고
 * <Button disabled={!isDirty}>저장</Button>
 * {isDirty && <p className="text-amber-500">저장되지 않은 변경사항이 있습니다.</p>}
 *
 * @example
 * // normalizeEmpty: true — null / undefined / "" 을 동일하게 처리
 * // API가 null을 반환하고 폼은 ""로 초기화할 때 유용
 * const { isDirty } = useDirtyCheck(original, form.values, { normalizeEmpty: true });
 */

"use client";

import { useMemo } from "react";

// ─── 내부 유틸 ────────────────────────────────────────────────────────────────

type AnyObj = Record<string, any>;

function normalize(v: unknown, empty: boolean): unknown {
    if (!empty) return v;
    return v === null || v === undefined || v === "" ? null : v;
}

/**
 * 문자열이 JSON 객체/배열처럼 보이면(맨 앞이 "{" 또는 "[") 파싱해서 돌려준다.
 * 파싱 실패하거나 객체/배열 모양이 아니면 원래 값을 그대로 돌려준다 — 일반 텍스트를
 * 잘못 건드리지 않는다 (예: 메모에 "{이름}" 같은 템플릿 문구가 있어도 안전).
 */
function tryParseJSON(v: unknown): unknown {
    if (typeof v !== "string") return v;
    const trimmed = v.trim();
    if (trimmed[0] !== "{" && trimmed[0] !== "[") return v;
    try {
        return JSON.parse(trimmed);
    } catch {
        return v;
    }
}

function deepEqual(a: unknown, b: unknown, emptyAsEqual: boolean): boolean {
    const na = normalize(a, emptyAsEqual);
    const nb = normalize(b, emptyAsEqual);

    if (na === nb) return true;
    if (na === null || nb === null) return false;

    // select 타입 필드처럼 폼 값은 항상 string 인데, 서버 원본은 number/object 등 원래
    // 타입 그대로인 경우(예: partner_key, address_json)가 흔하다. 문자열 쪽이 JSON
    // 객체/배열처럼 보이면 파싱해서(예: address_json 문자열 ↔ 원본 object) 구조적으로
    // 비교하고, 그래도 하나 이상이 원시값이면 타입 강제 없이 문자열로 변환해 비교한다
    // (number 3 ↔ string "3" 을 같은 값으로 취급) — 타입만 다르고 값은 같은데 "변경됨"
    // 으로 오판하는 걸 막는다.
    const pa = tryParseJSON(na);
    const pb = tryParseJSON(nb);
    if (pa === pb) return true;

    if (typeof pa !== "object" && typeof pb !== "object") {
        return String(pa) === String(pb);
    }
    if (typeof pa !== "object" || typeof pb !== "object") return false;

    if (Array.isArray(pa) !== Array.isArray(pb)) return false;

    if (Array.isArray(pa) && Array.isArray(pb)) {
        if (pa.length !== pb.length) return false;
        return pa.every((v, i) => deepEqual(v, pb[i], emptyAsEqual));
    }

    const aKeys = Object.keys(pa as AnyObj);
    const bKeys = Object.keys(pb as AnyObj);
    const allKeys = new Set([...aKeys, ...bKeys]);

    for (const k of allKeys) {
        if (!deepEqual((pa as AnyObj)[k], (pb as AnyObj)[k], emptyAsEqual)) return false;
    }
    return true;
}

// ─── 순수 함수 (React 없이 사용 가능) ────────────────────────────────────────

export interface DirtyCheckOptions {
    /**
     * null / undefined / ""를 모두 동일한 "빈 값"으로 처리한다.
     * API가 null을 반환하고 폼은 ""로 초기화하는 경우에 유용.
     * 기본값: false
     */
    normalizeEmpty?: boolean;
}

export interface DirtyCheckResult<T extends AnyObj> {
    /** 하나라도 변경된 필드가 있으면 true */
    isDirty: boolean;
    /** 필드별 변경 여부 — UI에서 하이라이트 표시 등에 사용 */
    dirtyFields: Partial<Record<keyof T, boolean>>;
    /** 변경된 필드만 담은 객체 — PATCH 요청에 사용 */
    diff: Partial<T>;
}

/**
 * 순수 함수 버전 — React 없이 서버 또는 이벤트 핸들러에서 사용 가능
 *
 * original 은 current 보다 필드가 많아도 된다 (예: API 원본 전체 타입 vs 폼에서 쓰는 일부 필드만 Pick 한 타입).
 * current 에 있는 키만 기준으로 비교하므로, 폼 모양에 맞춰 original 을 미리 추려낼 필요가 없다.
 */
export function getDirtyCheck<T extends AnyObj>(
    original: Partial<Record<keyof T, unknown>> | null | undefined,
    current: T,
    options: DirtyCheckOptions = {}
): DirtyCheckResult<T> {
    const emptyAsEqual = options.normalizeEmpty ?? false;

    if (!original) {
        return { isDirty: false, dirtyFields: {}, diff: {} };
    }

    const dirtyFields = {} as Partial<Record<keyof T, boolean>>;
    const diff = {} as Partial<T>;

    const keys = Object.keys(current) as (keyof T & string)[];

    for (const key of keys) {
        if (!deepEqual(original[key], current[key], emptyAsEqual)) {
            dirtyFields[key] = true;
            diff[key] = current[key];
        }
    }

    return {
        isDirty: Object.keys(dirtyFields).length > 0,
        dirtyFields,
        diff,
    };
}

// ─── React Hook ───────────────────────────────────────────────────────────────

/**
 * useDirtyCheck — 원본과 현재 값을 비교해 변경 여부를 반환하는 훅
 * original이 null/undefined이면 항상 isDirty=false (데이터 로딩 중 처리)
 */
export function useDirtyCheck<T extends AnyObj>(
    original: Partial<Record<keyof T, unknown>> | null | undefined,
    current: T,
    options: DirtyCheckOptions = {}
): DirtyCheckResult<T> {
    return useMemo(
        () => getDirtyCheck(original, current, options),
        // options 객체는 렌더마다 새로 생성되므로 값만 의존성으로 사용
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [original, current, options.normalizeEmpty]
    );
}
